import { Injectable, inject, signal } from '@angular/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth, UserClaims } from '@okta/okta-auth-js';

import { EngagementMessengerService } from '@core/services/engagement-messenger.service';
import { environment } from '@env/environment';

export interface ImpersonatedUserContext {
  readonly name: string;
  readonly email: string;
  readonly sysId: string;
}

export interface PersonaDashboard {
  readonly dashboardSysId: string;
  readonly name: string;
  readonly workspace: string;
  readonly chart: string;
  readonly section: string;
  readonly env?: string;
  readonly key?: string;
}

export interface PersonaPermissions {
  readonly personaSysId: string;
  readonly name: string;
  readonly dashboards?: readonly PersonaDashboard[];
  readonly actionInsights: boolean;
  readonly priorityAction: boolean;
  readonly aiCockpit: boolean;
  readonly invoice: boolean;
  readonly raid: boolean;
  readonly chatbot: boolean;
  readonly delegation: boolean;
  readonly powerBi: boolean;
}

export type PersonaPermissionKey = keyof Omit<
  PersonaPermissions,
  'personaSysId' | 'name' | 'dashboards'
>;

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly oktaAuth = inject(OKTA_AUTH) as OktaAuth;
  private readonly engagementMessenger = inject(EngagementMessengerService);
  private readonly impersonationStorageKey = 'governx.impersonatedUser';

  private readonly isAuthenticatedState = signal(false);
  private readonly userDetailsState = signal<UserClaims | null>(null);
  private readonly accessTokenState = signal('');
  private readonly impersonatedUserNameState = signal('');
  private readonly impersonatedEmailState = signal('');
  private readonly impersonatedUserSysIdState = signal('');
  private readonly personaDetailsState = signal<PersonaPermissions | null>(null);

  readonly isAuthenticated = this.isAuthenticatedState.asReadonly();
  readonly userDetails = this.userDetailsState.asReadonly();
  readonly accessToken = this.accessTokenState.asReadonly();
  readonly impersonatedUserName = this.impersonatedUserNameState.asReadonly();
  readonly impersonatedEmail = this.impersonatedEmailState.asReadonly();
  readonly impersonatedUserSysId = this.impersonatedUserSysIdState.asReadonly();
  readonly personaDetails = this.personaDetailsState.asReadonly();

  async loadSession(): Promise<void> {
    const isAuthenticated = await this.oktaAuth.isAuthenticated();
    this.isAuthenticatedState.set(isAuthenticated);

    if (!isAuthenticated) {
      this.userDetailsState.set(null);
      this.accessTokenState.set('');
      this.restoreImpersonatedUser();
      return;
    }

    const [user, accessToken] = await Promise.all([
      this.oktaAuth.getUser(),
      this.oktaAuth.getAccessToken(),
    ]);

    this.userDetailsState.set(user);
    this.accessTokenState.set(accessToken ?? '');
    this.restoreImpersonatedUser();
  }

  async getAccessToken(): Promise<string> {
    const cachedAccessToken = this.accessTokenState();
    if (cachedAccessToken) {
      return cachedAccessToken;
    }

    const accessToken = await this.oktaAuth.getAccessToken();
    const resolvedToken = accessToken ?? '';
    this.accessTokenState.set(resolvedToken);
    return resolvedToken;
  }

  async getUserInfo(): Promise<UserClaims | null> {
    const cachedUserDetails = this.userDetailsState();
    if (cachedUserDetails) {
      return cachedUserDetails;
    }

    const isAuthenticated = await this.oktaAuth.isAuthenticated();
    if (!isAuthenticated) {
      return null;
    }

    const userDetails = await this.oktaAuth.getUser();
    this.userDetailsState.set(userDetails);
    return userDetails;
  }

  async getUserEmail(): Promise<string> {
    this.syncImpersonatedUserFromStorage();

    const impersonatedEmail = this.impersonatedEmailState().trim();
    if (impersonatedEmail) {
      return impersonatedEmail;
    }

    const userDetails = await this.getUserInfo();
    const email = userDetails?.email;
    return typeof email === 'string' ? email : '';
  }

  hasImpersonatedSession(): boolean {
    this.syncImpersonatedUserFromStorage();
    return this.impersonatedEmailState().trim().length > 0;
  }

  async getLoggedInUserEmail(): Promise<string> {
    const userDetails = await this.getUserInfo();
    const email = userDetails?.email;
    return typeof email === 'string' ? email.trim() : '';
  }

  setImpersonatedEmail(email: string | null | undefined): void {
    this.setImpersonatedUser({
      name: '',
      email: email?.trim() ?? '',
      sysId: '',
    });
  }

  setImpersonatedUser(user: Partial<ImpersonatedUserContext> | null | undefined): void {
    const resolvedName = user?.name?.trim() ?? '';
    const resolvedEmail = user?.email?.trim() ?? '';
    const resolvedSysId = user?.sysId?.trim() ?? '';

    this.impersonatedUserNameState.set(resolvedName);
    this.impersonatedEmailState.set(resolvedEmail);
    this.impersonatedUserSysIdState.set(resolvedSysId);

    this.persistImpersonatedUser({
      name: resolvedName,
      email: resolvedEmail,
      sysId: resolvedSysId,
    });
  }

  getUserSysId(): string {
    return this.impersonatedUserSysIdState();
  }

  // Keeps the effective-user sys_id in sync with whatever UserDetailsService resolves from the
  // user-details API, without overwriting the name/email already tracked for this session.
  updateResolvedUserSysId(sysId: string): void {
    const trimmedSysId = sysId.trim();
    if (!trimmedSysId || this.impersonatedUserSysIdState() === trimmedSysId) {
      return;
    }

    this.setImpersonatedUser({
      name: this.impersonatedUserNameState(),
      email: this.impersonatedEmailState(),
      sysId: trimmedSysId,
    });
  }

  clearImpersonatedEmail(): void {
    this.impersonatedUserNameState.set('');
    this.impersonatedEmailState.set('');
    this.impersonatedUserSysIdState.set('');
    this.persistImpersonatedUser(null);
  }

  setPersonaDetails(persona: PersonaPermissions | null): void {
    const wasChatbotEnabled = this.personaDetailsState()?.chatbot ?? false;
    this.personaDetailsState.set(persona);

    if (persona?.chatbot) {
      if (!wasChatbotEnabled) {
        this.initializeEngagementMessenger();
      }
      return;
    }

    if (wasChatbotEnabled) {
      this.engagementMessenger.notifyLogout();
    }
  }

  clearPersonaDetails(): void {
    this.setPersonaDetails(null);
  }

  hasPersonaAccess(permission: PersonaPermissionKey): boolean {
    return this.personaDetailsState()?.[permission] ?? false;
  }

  async logout(): Promise<void> {
    const isOktaSession = await this.oktaAuth.isAuthenticated();

    this.isAuthenticatedState.set(false);
    this.userDetailsState.set(null);
    this.accessTokenState.set('');
    this.clearImpersonatedEmail();
    this.clearPersonaDetails();

    if (!isOktaSession) {
      this.clearAllBrowserStorage();
      this.redirectToHome();
      return;
    }

    await this.oktaAuth.signOut({
      postLogoutRedirectUri: `${window.location.origin}/`,
    });
  }

  private initializeEngagementMessenger(): void {
    if (typeof window.initServiceNowEngagementMessenger !== 'function') {
      console.error('[AuthSession] ServiceNow Engagement Messenger initializer is unavailable');
      return;
    }

    console.log('[AuthSession] Initializing ServiceNow Engagement Messenger');
    try {
      window.initServiceNowEngagementMessenger(environment.engagementMessenger.moduleId);
      this.engagementMessenger.notifyLogin();
    } catch (error) {
      console.error('[AuthSession] ServiceNow Engagement Messenger initialization failed', error);
    }
  }

  private restoreImpersonatedUser(): void {
    this.syncImpersonatedUserFromStorage();
  }

  private syncImpersonatedUserFromStorage(): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    let rawValue = storage.getItem(this.impersonationStorageKey);
    if (!rawValue) {
      const legacySessionValue = this.getLegacySessionStorageValue();
      if (legacySessionValue) {
        rawValue = legacySessionValue;
        storage.setItem(this.impersonationStorageKey, legacySessionValue);
      }
    }

    if (!rawValue) {
      this.impersonatedUserNameState.set('');
      this.impersonatedEmailState.set('');
      this.impersonatedUserSysIdState.set('');
      return;
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<ImpersonatedUserContext>;
      this.impersonatedUserNameState.set(parsed?.name?.trim() ?? '');
      this.impersonatedEmailState.set(parsed?.email?.trim() ?? '');
      this.impersonatedUserSysIdState.set(parsed?.sysId?.trim() ?? '');
    } catch {
      storage.removeItem(this.impersonationStorageKey);
      this.removeLegacySessionStorageValue();
      this.impersonatedUserNameState.set('');
      this.impersonatedEmailState.set('');
      this.impersonatedUserSysIdState.set('');
    }
  }

  private persistImpersonatedUser(user: ImpersonatedUserContext | null): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    if (!user || !user.email.trim()) {
      storage.removeItem(this.impersonationStorageKey);
      this.removeLegacySessionStorageValue();
      return;
    }

    storage.setItem(this.impersonationStorageKey, JSON.stringify(user));
    this.removeLegacySessionStorageValue();
  }

  private getStorage(): Storage | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return window.localStorage;
  }

  private getLegacySessionStorageValue(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    return window.sessionStorage.getItem(this.impersonationStorageKey) ?? '';
  }

  private removeLegacySessionStorageValue(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.removeItem(this.impersonationStorageKey);
  }

  private clearAllBrowserStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.clear();
    window.sessionStorage.clear();
  }

  private redirectToHome(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.location.assign(`${window.location.origin}/`);
  }
}
